import React, { useState, useEffect, useRef } from "react";
import { useKeenSlider } from "keen-slider/react";
import "keen-slider/keen-slider.min.css";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

const services = [
    {
        id: 1,
        title: "AI & Automation",
        links: "/ai-automation",
        category: "service",
        desc: "We help businesses work smarter using AI and automation, making everyday operations easier, cutting unnecessary costs, and supporting steady, long-term growth in a practical way.",
        defaultBg: "bg-[radial-gradient(circle_at_0%_0%,#FFFFFF_0%,#8DA2FF_45%,#00106B_100%)]",
        hoverBg: "bg-[radial-gradient(circle_at_0%_0%,#8DA2FF_0%,#00106B_60%,#000000_100%)]",
        image: "/assets/service-detail/img1.png"
    },
    {
        id: 3,
        title: "Product Engineering",
        links: "/product-engineering",
        category: "service",
        desc: "We help turn ideas into real products designing, building, and scaling them so they can succeed in global markets and make a real impact.",
        defaultBg: "bg-[radial-gradient(circle_at_0%_0%,#F8FAFC_0%,#94A3B8_40%,#0F172A_100%)]",
        hoverBg: "bg-[radial-gradient(circle_at_0%_0%,#64748B_0%,#1E293B_60%,#020617_100%)]",
        image: "/assets/service-detail/img3.png"
    },
    {
        id: 5,
        title: "ServiceNow Consulting",
        links: "/servicenow-consulting",
        category: "service",
        desc: "Simplify your operations with expert ServiceNow implementation and trusted consulting support.",
        defaultBg: "bg-[radial-gradient(circle_at_0%_0%,#F0FDFA_0%,#5EEAD4_40%,#115E59_100%)]",
        hoverBg: "bg-[radial-gradient(circle_at_100%_0%,#2DD4BF_0%,#0F766E_60%,#134E4A_100%)]",
        image: "/assets/service-detail/img5.png"
    },
    {
        id: 2,
        links: "/cloud-engineering",
        title: "Cloud Engineering",
        category: "service",
        desc: "We help you design, build, and scale secure cloud systems that fit your business and grow with it smoothly.",
        defaultBg: "bg-[radial-gradient(circle_at_0%_0%,#FFFFFF_0%,#7DD3FC_40%,#01386A_100%)]",
        hoverBg: "bg-[radial-gradient(circle_at_100%_0%,#38BDF8_0%,#0369A1_60%,#0C4A6E_100%)]",
        image: "/assets/service-detail/img2.png"
    },
    {
        id: 9,
        title: "Industrial Automation",
        links: "/industrial-automation",
        category: "service",
        desc: "We help industries run smarter by building simple, reliable automation systems and designing control solutions that keep operations smooth, efficient, and easy to manage.",
        defaultBg: "bg-[radial-gradient(circle_at_0%_0%,#E0F2FE_0%,#38BDF8_45%,#0C4A6E_100%)]",
        hoverBg: "bg-[radial-gradient(circle_at_100%_100%,#0EA5E9_0%,#0369A1_60%,#082F49_100%)]",
        image: "/assets/service-detail/img9.jpg"
    },
    {
        id: 7,
        title: "MES Solutions",
        links: "/manufacturing-execution-system",
        category: "service",
        desc: "We help you understand where you stand, identify gaps, and create a practical roadmap with a strong business case to successfully adopt or improve MES.",
        defaultBg: "bg-[radial-gradient(circle_at_0%_0%,#F5F3FF_0%,#A78BFA_40%,#2E1065_100%)]",
        hoverBg: "bg-[radial-gradient(circle_at_0%_0%,#8B5CF6_0%,#4C1D95_60%,#2E1065_100%)]",
        image: "/assets/service-detail/img7.png"
    },
    {
        id: 6,
        title: "Infor M3 Solutions",
        links: "/infor-m3-solutions",
        category: "service",
        desc: "Helping enterprises grow with tailored Infor M3 ERP solutions and dependable support that simplify operations and improve everyday business efficiency.",
        defaultBg: "bg-[radial-gradient(circle_at_0%_0%,#F1F5F9_0%,#64748B_50%,#0F172A_100%)]",
        hoverBg: "bg-[radial-gradient(circle_at_50%_0%,#475569_0%,#1E293B_60%,#020617_100%)]",
        image: "/assets/service-detail/img6.png"
    },
    // {
    //     id: 8,
    //     title: "Building Automation",
    //     links: "/bms-soluction",
    //     category: "service",
    //     desc: "We provide complete Building Automation and BMS solutions that make your buildings smarter, easier to manage, and more energy-efficient.",
    //     defaultBg: "bg-[radial-gradient(circle_at_0%_0%,#EFF6FF_0%,#60A5FA_45%,#172554_100%)]",
    //     hoverBg: "bg-[radial-gradient(circle_at_100%_50%,#3B82F6_0%,#1D4ED8_60%,#0E2A62_100%)]",
    //     image: "/assets/service-detail/img8.png"
    // },
    {
        id: 4,
        title: "Performance Engineering",
        links: "/performance-engineering",
        category: "service",
        desc: "Improve your systems so they run smoothly, stay reliable, and perform at their best every day.",
        defaultBg: "bg-[radial-gradient(circle_at_0%_0%,#E0E7FF_0%,#818CF8_45%,#1E1B4B_100%)]",
        hoverBg: "bg-[radial-gradient(circle_at_0%_100%,#6366F1_0%,#312E81_60%,#000000_100%)]",
        image: "/assets/service-detail/img4.png"
    },
];

const TechServicesSection = () => {
    const [loaded, setLoaded] = useState(false);

    const [sliderRef, instanceRef] = useKeenSlider(
        {
            initial: 0,
            created() {
                setLoaded(true);
            },
            loop: true,
            mode: "snap",
            slides: {
                perView: 1.2,
                spacing: 16,
            },
            breakpoints: {
                "(min-width: 640px)": { slides: { perView: 2.2, spacing: 20 } },
                "(min-width: 1024px)": { slides: { perView: 3.6, spacing: 20 } },
                "(min-width: 1280px)": { slides: { perView: 4.0, spacing: 20 } },
            },

        },
        [
            (slider) => {
                let timeout;
                let mouseOver = false;

                function clearNextTimeout() {
                    clearTimeout(timeout);
                }

                function nextTimeout() {
                    clearTimeout(timeout);
                    if (mouseOver) return;
                    timeout = setTimeout(() => {
                        slider.next();
                    }, 2000);
                }

                slider.on("created", () => {
                    slider.container.addEventListener("mouseover", () => {
                        mouseOver = true;
                        clearNextTimeout();
                    });
                    slider.container.addEventListener("mouseout", () => {
                        mouseOver = false;
                        nextTimeout();
                    });
                    nextTimeout();
                });
                slider.on("dragStarted", clearNextTimeout);
                slider.on("animationEnded", nextTimeout);
                slider.on("updated", nextTimeout);
            },
        ]

    );

    return (
        <section className="py-[40px] lg:py-[80px] bg-white font-sans relative overflow-hidden flex flex-col justify-center">
            <div className="absolute inset-0 z-0 opacity-40 pointer-events-none">
                <FlowingMeshCanvas />
            </div>

            <div className="max-w-[1220px] w-full mx-auto px-[20px] lg:px-[80px] relative z-10">
                <h2 className="text-[#01386A] font-sora text-[20px] lg:text-[30px] font-[600] lg:mb-[60px] mb-[30px] relative z-10 leading-[1.2]">
                    We turn complexity into clarity and results
                </h2>

                <div ref={sliderRef} className="keen-slider py-10 -my-10">
                    {services.map((service) => (
                        <div key={service.id} className="keen-slider__slide">
                            <Link to={service?.links}>
                                <ServiceCard service={service} />
                            </Link>
                        </div>
                    ))}
                </div>

                {loaded && instanceRef.current && (
                    <div className="flex items-center gap-4 mt-[36px] justify-start lg:justify-start">
                        <NavButton
                            onClick={(e) => e.stopPropagation() || instanceRef.current?.prev()}
                            icon={<ArrowLeft size={20} />}
                        />
                        <NavButton
                            onClick={(e) => e.stopPropagation() || instanceRef.current?.next()}
                            icon={<ArrowRight size={20} />}
                        />
                    </div>
                )}
            </div>
        </section>
    );
};

const ServiceCard = ({ service }) => {
    return (
        <motion.div
            initial="initial"
            whileHover="hover"
            animate="initial"
            className="relative h-[400px] sm:h-[450px] lg:h-[480px] overflow-hidden cursor-pointer group shadow-lg rounded-sm"
        >
            <div className={`absolute inset-0 z-0 ${service.defaultBg}`} />
            <motion.div
                className={`absolute inset-0 z-1 ${service.hoverBg}`}
                variants={{
                    initial: { opacity: 0 },
                    hover: { opacity: 1 }
                }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
            />

            <motion.div
                className="absolute bottom-0 right-0 z-10 w-[100%] h-[240px] sm:h-[280px] lg:h-[300px] flex items-end justify-center pointer-events-none"
                variants={{
                    initial: { x: 0, opacity: 1 },
                    hover: { x: -300, opacity: 0 }
                }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
            >
                {service.image ? (
                    <>
                        <img
                            src={service.image}
                            alt={service.title}
                            className="w-full h-full object-cover object-bottom drop-shadow-2xl"
                        />
                        <div className="absolute inset-0 bg-black/40 z-10" />
                    </>
                ) : null}
            </motion.div>

            <div className="relative z-20 h-full flex flex-col p-[20px] sm:p-[30px]">
                <div className="relative z-20">
                    <h3 className="text-[#000000] font-sora font-[500] text-[18px] lg:text-[20px] leading-[1.2] lg:leading-[1.1] transition-colors duration-300 group-hover:text-white pt-2 sm:pt-3">
                        {service.title}
                    </h3>
                </div>

                <div className="flex-1 flex items-start pt-[20px] sm:pt-[30px] relative overflow-hidden">
                    <motion.p
                        className="text-[#000000] font-sora text-[14px] sm:text-[16px] leading-[22px] sm:leading-[26px] transition-colors duration-300 group-hover:text-white"
                        variants={{
                            initial: { x: 50, opacity: 0 },
                            hover: { x: 0, opacity: 1 }
                        }}
                        transition={{ duration: 0.4, ease: "easeOut", delay: 0.1 }}
                    >
                        {service.desc}
                    </motion.p>
                </div>

                <motion.div
                    className="flex items-center justify-end mt-auto"
                    variants={{
                        initial: { y: 20, opacity: 0 },
                        hover: { y: 0, opacity: 1 }
                    }}
                    transition={{ duration: 0.4, delay: 0.1 }}
                >
                    <div className="group flex items-center gap-2 text-white font-graphik font-[500] text-[15px] sm:text-[16px]">
                        <span className="opacity-90 group-hover:opacity-100 transition-opacity">Explore</span>
                        <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
                    </div>
                </motion.div>
            </div>

            <div className="absolute inset-0 border border-white/10 pointer-events-none z-30" />
        </motion.div>
    );
};

const FlowingMeshCanvas = () => {
    const canvasRef = useRef(null);
    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        let animationFrameId;
        let time = 0;
        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        window.addEventListener("resize", resizeCanvas);
        resizeCanvas();
        const draw = () => {
            const { width, height } = canvas;
            ctx.clearRect(0, 0, width, height);
            const lines = 50;
            const amplitude = 80;
            const frequency = 0.002;
            const speed = 0.003;
            ctx.lineWidth = 0.8;
            for (let i = 0; i < lines; i++) {
                ctx.beginPath();
                const alpha = 0.1 + (i / lines) * 0.3;
                ctx.strokeStyle = `hsla(217, 54%, 24%, ${alpha})`;
                for (let x = 0; x <= width; x += 5) {
                    const yOffset = height / 2;
                    const y1 = Math.sin(x * frequency + time + i * 0.05) * amplitude;
                    const y2 = Math.sin(x * frequency * 0.5 + time * 0.5) * (amplitude * 0.5);
                    const tilt = (x / width) * 50;
                    ctx.lineTo(x, yOffset + y1 + y2 + tilt + (i * 3));
                }
                ctx.stroke();
            }
            time += speed;
            animationFrameId = requestAnimationFrame(draw);
        };
        draw();
        return () => {
            window.removeEventListener("resize", resizeCanvas);
            cancelAnimationFrame(animationFrameId);
        };
    }, []);
    return <canvas ref={canvasRef} className="w-full h-full" />;
};

const NavButton = ({ onClick, icon }) => (
    <button
        onClick={onClick}
        className="w-[32px] h-[32px] rounded-full border border-[#ED6800] text-[#ED6800] flex items-center justify-center hover:bg-[#ED6800] hover:text-white transition-all duration-300 relative z-20"
    >
        {icon}
    </button>
);


export default TechServicesSection;